import { NavLink } from 'react-router-dom';
import { useYourTurnCount } from '../modules/games';
import { NavIcon } from './icons/NavIcon';
import { getChromeNavEntries } from './navEntries';

/** Rendered separately as the gear icon next to the sync indicator (`AppShell.tsx`), not
 * as a peer nav item — see the "Settings placement" note in docs/DECISIONS.md. */
const SETTINGS_PATH = '/settings';

/**
 * Traditional top nav bar, desktop only (`lg:` — 1024px, same breakpoint `BottomNav` uses
 * for `lg:hidden`, so there's no dead zone where neither renders). Owner call
 * (2026-07-31): a top bar reads more "this is the app's home" than a sidebar, and it frees
 * the full content width instead of taking a 224px rail on every desktop screen.
 *
 * Registry-driven, same `hideInTabs` opt-out as the mobile tab bar (digest is the app's
 * home — reachable from the wordmark — so it doesn't need its own slot here either).
 * Household, Install and the temporary Dev entry no longer register a `nav` at all (owner
 * call, 2026-07-31) — they're reachable from Settings instead, so today this renders
 * exactly the four primary destinations: Calendar, Lists, Recipes, Check-in.
 */
export function TopNav(): JSX.Element {
  const yourTurn = useYourTurnCount();
  const entries = getChromeNavEntries().filter(
    (entry) => !entry.hideInTabs && entry.path !== SETTINGS_PATH,
  );

  return (
    <nav aria-label="Primary" className="hidden lg:block">
      <ul className="flex h-16 items-stretch">
        {entries.map((entry) => (
          <li key={entry.path} className="flex">
            <NavLink
              to={entry.path}
              className={({ isActive }) =>
                [
                  'flex items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors duration-150 ease-app',
                  isActive ? 'border-fg text-fg' : 'border-transparent text-fg-3 hover:text-fg-2',
                ].join(' ')
              }
            >
              <span className="relative">
                <NavIcon moduleId={entry.moduleId} emoji={entry.icon} size={18} />
                {entry.moduleId === 'games' && yourTurn > 0 && (
                  <span className="absolute -right-2 -top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-accent px-0.5 text-[9px] font-bold text-inverse-fg">
                    {yourTurn}
                  </span>
                )}
              </span>
              {entry.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
